import {
  AddressInput,
  Button,
  CountryInput,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  SingleFileUploader,
} from '@commenda/kakashi';
import { defaultAddressSchema } from '@commenda/schema/address';
import { IAddress } from '@commenda/types';
import { FileBasic } from '@commenda/types/src/file/types';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { classNames, fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { LegacyDocumentType } from '@prisma/client';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import { DirectorFormType } from '../Director/DirectorFormType';
import { getIdentityProof } from '../in/utils';
import { directorSchema, DirectorSchemaFields } from './schema';

const DirectorForm: DirectorFormType<DirectorBase> = ({
  director,
  companyDataMutate,
  setOpen,
  isAddingDirector,
  setIsAddingDirector,
  companyId,
  corporationId,
  token,
  setNotification,
  onErrorResolve,
  selectedDirectorIds,
  setSelectedDirectorIds,
  modalConfig,
  workRequestData,
}: {
  director?: DirectorBase;
  companyDataMutate: KeyedMutator<unknown>;
  isAddingDirector: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAddingDirector: React.Dispatch<React.SetStateAction<boolean>>;
  companyId: number;
  corporationId?: number;
  token: string;
  setNotification: TSetNotification;
  onErrorResolve?: () => void;
  selectedDirectorIds?: number[];
  setSelectedDirectorIds?: React.Dispatch<React.SetStateAction<number[]>>;
  modalConfig?: {
    heading?: string;
    subheading?: string;
    isDirector?: boolean;
    isAuthorizedSignatory?: boolean;
    postSave?: (director: {
      id: number;
      directorFirstName: string;
      directorLastName: string;
    }) => void;
  };
  workRequestData?: { id: string };
}) => {
  const passportProofFile = getIdentityProof(director, corporationId);
  const [passportProof, setPassportProof] = useState<FileBasic | null>(
    passportProofFile
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    clearErrors,
    setError,
  } = useForm<DirectorSchemaFields>({
    resolver: zodResolver(directorSchema),
    defaultValues: {
      directorFirstName: director?.directorFirstName,
      directorLastName: director?.directorLastName,
      email: director?.email,
      operatingAddress: director?.operatingAddress || defaultAddressSchema,
      personalDetails: {
        nationality: director?.personalDetails?.nationality,
      },
      passportProof: passportProofFile,
    },
  });

  useEffect(() => {
    if (passportProof) {
      setValue('passportProof', passportProof);
    }
  }, [passportProof]);

  const onSubmit = async (data: DirectorSchemaFields) => {
    setIsAddingDirector(true);

    const workRequestId = workRequestData?.id;

    const res = await fetcher(
      director?.id ? `director/${director.id}` : 'director',
      {
        method: 'POST',
        body: {
          directorFirstName: data.directorFirstName,
          directorLastName: data.directorLastName,
          email: data.email,
          personalDetails: {
            nationality: data?.personalDetails?.nationality,
          },
          operatingAddress: data.operatingAddress,
          filesToAdd: [
            {
              // Use existing file ID if available, otherwise use -1 for new uploads
              id: passportProof?.id ?? passportProofFile?.id ?? -1,
              documentType: LegacyDocumentType.Passport,
              fileName: passportProof?.fileName,
              fileUrl: passportProof?.fileUrl,
            },
          ],
          corporationHasDirector: [
            {
              corporationId,
              additionalDirectorInformation: {
                ...(modalConfig?.isDirector ? { isDirector: true } : {}),
                ...(modalConfig?.isAuthorizedSignatory
                  ? { isAuthorizedSignatory: true }
                  : {}),
              },
            },
          ],
          companyId,
          ...(workRequestId != null ? { wrkReqId: workRequestId } : {}),
        },
      },
      token
    );

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again later or contact team Commenda if you need help',
      });

      await companyDataMutate();
      setIsAddingDirector(false);
      return;
    }

    if (res.status === 409) {
      const error = await res.json();
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: error.message,
      });
      setIsAddingDirector(false);
      return;
    }

    await companyDataMutate();

    const resData = await res.json();

    if (setSelectedDirectorIds) {
      setSelectedDirectorIds([...(selectedDirectorIds || []), resData.id]);
    }

    if (onErrorResolve) onErrorResolve();

    setIsAddingDirector(false);
    reset();
    setOpen(false);

    if (modalConfig?.postSave) {
      modalConfig.postSave({
        id: resData.id,
        directorFirstName: resData.directorFirstName,
        directorLastName: resData.directorLastName,
      });
    }
  };

  return (
    <form
      className="flex flex-col max-w-[574px]"
      onSubmit={handleSubmit(onSubmit, (err) => {
        console.error(err);
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Please enter all the required details.',
        });
      })}
    >
      <DialogHeader className="p-4">
        <DialogTitle>
          {director
            ? 'Edit director'
            : modalConfig?.heading || 'Add a new director'}
          <p className="text-sm font-normal mt-1 text-neutral-2">
            {modalConfig?.subheading ?? ''}
          </p>
        </DialogTitle>
      </DialogHeader>

      <div className="grow shrink-0 flex flex-col gap-4 overflow-y-auto max-h-[calc(75vh-163px)] px-4 py-6">
        <div className="flex flex-col gap-4">
          <FieldWrapper>
            <Label className="text-sm gap-0.5 text-neutral-1">
              <span>Legal Name (as on your passport)</span>
              <span className="ml-0.5 text-utility-violet-600">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="First name"
                {...register('directorFirstName')}
                error={errors?.directorFirstName?.message}
                defaultValue={watch('directorFirstName')}
              />
              <Input
                placeholder="Last name"
                {...register('directorLastName')}
                error={errors?.directorLastName?.message}
                defaultValue={watch('directorLastName')}
              />
            </div>
          </FieldWrapper>

          <div className="grid grid-cols-2 gap-2">
            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Email <span className="ml-0.5 text-utility-violet-600">*</span>
              </Label>

              <Input
                type="email"
                placeholder="Email"
                {...register('email')}
                error={errors?.email?.message}
                value={watch('email')}
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Nationality
                <span className="ml-0.5 text-utility-violet-600">*</span>
              </Label>

              <CountryInput
                placeholder="Search country"
                type="single"
                onValueChange={(ctr) => {
                  setValue('personalDetails.nationality', ctr);
                }}
                {...register('personalDetails.nationality')}
                error={
                  errors?.personalDetails?.nationality?.message &&
                  !watch('personalDetails.nationality')
                    ? errors?.personalDetails?.nationality?.message
                    : undefined
                }
                value={watch('personalDetails.nationality')}
              />
            </FieldWrapper>
          </div>
        </div>

        <FieldWrapper>
          <Label>
            Current Address{' '}
            <span className="ml-0.5 text-utility-violet-600">*</span>
          </Label>
          <AddressInput
            address={watch('operatingAddress') as IAddress}
            onChange={(ad) => {
              setValue('operatingAddress', ad);
              if (ad.country !== watch('operatingAddress.country')) {
                clearErrors('operatingAddress.state');
              }
            }}
            lastRowClassName="grid-cols-2"
            error={{
              country:
                errors?.operatingAddress?.country?.message &&
                !watch('operatingAddress.country')
                  ? errors?.operatingAddress?.country?.message
                  : undefined,
              addressLine1:
                !watch('operatingAddress.addressLine1') &&
                errors?.operatingAddress?.addressLine1?.message
                  ? errors?.operatingAddress?.addressLine1?.message
                  : null,
              addressLine2:
                !watch('operatingAddress.addressLine2') &&
                errors?.operatingAddress?.addressLine2?.message
                  ? errors?.operatingAddress?.addressLine2?.message
                  : null,
              city:
                !watch('operatingAddress.city') &&
                errors?.operatingAddress?.city?.message
                  ? errors?.operatingAddress?.city?.message
                  : null,
              postalCode:
                !watch('operatingAddress.postalCode') &&
                errors?.operatingAddress?.postalCode?.message
                  ? errors?.operatingAddress?.postalCode?.message
                  : null,
              state:
                !watch('operatingAddress.state') &&
                errors?.operatingAddress?.state?.message
                  ? errors?.operatingAddress?.state?.message
                  : undefined,
            }}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>
            Upload Passport
            <span className="text-violet-solid ml-1">*</span>
          </Label>
          <SingleFileUploader
            companyId={companyId}
            setFile={(file) => {
              setPassportProof(file);
              clearErrors('passportProof');
            }}
            file={passportProof}
            error={errors?.passportProof?.message}
            setError={(msg: string) =>
              setError('passportProof', { type: 'custom', message: msg })
            }
            token={token}
            accept={{
              'application/pdf': ['.pdf'],
              'image/*': ['.png', '.jpg', '.jpeg'],
            }}
            maxSize={10 * 1024 * 1024} // 10MB
            documentType={LegacyDocumentType.Passport}
            _className="w-full"
          />
          {errors?.passportProof ||
          errors?.passportProof?.fileName ||
          errors?.passportProof?.fileUrl ? (
            <InputErrorMessage message="Please upload your passport." />
          ) : null}
        </FieldWrapper>
      </div>

      <DialogFooter className="flex justify-end gap-3 p-4">
        <Button
          variant="secondary"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            setOpen(false);
          }}
        >
          <span>Cancel</span>
        </Button>
        <Button type="submit" variant="primary">
          {isAddingDirector ? <LoadingIndicator size={32} /> : null}
          <span
            className={classNames(
              'flex items-center',
              isAddingDirector ? 'ml-2' : ''
            )}
          >
            Save
          </span>
        </Button>
      </DialogFooter>
    </form>
  );
};

export default DirectorForm;
